var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * Copyright (c) 2026 LS ELECTRIC Co. Ltd. All Reserved.
 */
/*******************************************************************
 * @brief 로그인 이상징후 판별용 보안문자(CAPTCHA) 입력 컴포넌트
 *
 * 비밀번호 연속 불일치가 임계치(기본 4회) 이상인 계정의 로그인 시에만 노출된다.
 * 서버가 숫자 6자리 이미지를 생성해 base64 로 내려주며, 발급키(captchaKey)는
 * 1회용이므로 로그인이 실패할 때마다 새로 발급받아야 한다.
 * (부모가 reloadTrigger 값을 바꾸면 재발급된다)
 ********************************************************************/
import { useEffect, useState } from "react";
import * as HttpUtil from "../../utils/api/HttpUtil";
function Captcha({ value, onChange, onCaptchaKeyChange, reloadTrigger = 0, onKeyDown, isError = false, placeholder = "보안문자 6자리 입력", }) {
    const [image, setImage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (() => __awaiter(this, void 0, void 0, function* () {
            setIsLoading(true);
            const res = yield HttpUtil.PromiseHttp({
                httpMethod: "GET",
                appPath: "/auth/captcha",
                appQuery: {},
            });
            if (cancelled)
                return;
            if (res && res.codeNum == 200 && res.body) {
                setImage(res.body.image);
                onCaptchaKeyChange(res.body.captchaKey);
            }
            onChange(""); // 재발급 시 이전 입력값은 무효
            setIsLoading(false);
        }))();
        return () => {
            cancelled = true;
        };
        // reloadTrigger 가 바뀔 때만 재발급한다. (콜백은 매 렌더 새로 생성되므로 의존성에서 제외)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTrigger]);
    /** 이미지 클릭 / 새로고침 버튼 - 서버에서 새 CAPTCHA 를 받아온다 */
    const refresh = () => __awaiter(this, void 0, void 0, function* () {
        if (isLoading)
            return;
        setIsLoading(true);
        const res = yield HttpUtil.PromiseHttp({
            httpMethod: "GET",
            appPath: "/auth/captcha",
            appQuery: {},
        });
        if (res && res.codeNum == 200 && res.body) {
            setImage(res.body.image);
            onCaptchaKeyChange(res.body.captchaKey);
        }
        onChange("");
        setIsLoading(false);
    });
    return (
    // .form__input li 는 display:flex(가로)이므로, 이미지+입력란을 하나의 세로 컨테이너로 묶어
    // flex 아이템 하나로 만들어야 아래로 쌓인다.
    // 위 비밀번호 필드의 오류 문구(.input-errortxt, position:absolute)가 2줄이라
    // 이미지와 겹치므로, 그만큼 위쪽 여백을 확보한다.
    _jsxs("li", Object.assign({ style: { marginTop: "0.5rem" } }, { children: [_jsx("label", Object.assign({ htmlFor: "captchaValue", className: "hide" }, { children: "\uBCF4\uC548\uBB38\uC790 \uC785\uB825" })), _jsxs("div", Object.assign({ style: { display: "flex", flexDirection: "column", alignItems: "stretch", gap: "8px", width: "100%" } }, { children: [_jsxs("div", Object.assign({ style: { display: "flex", alignItems: "center", gap: "8px", width: "100%" } }, { children: [image ? (_jsx("img", { src: image, alt: "\uBCF4\uC548\uBB38\uC790 \uC774\uBBF8\uC9C0", title: "\uD074\uB9AD\uD558\uBA74 \uC0C8 \uBCF4\uC548\uBB38\uC790\uB97C \uBC1B\uC2B5\uB2C8\uB2E4", onClick: refresh, style: {
                                    flex: "1 1 auto",
                                    minWidth: "0",
                                    boxSizing: "border-box",
                                    height: "44px",
                                    border: "1px solid #d8d8d8",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                } })) : (_jsx("div", Object.assign({ style: {
                                    flex: "1 1 auto",
                                    minWidth: "0",
                                    boxSizing: "border-box",
                                    height: "44px",
                                    border: "1px solid #d8d8d8",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    color: "#999",
                                } }, { children: isLoading ? "불러오는 중..." : "이미지를 불러올 수 없습니다" }))), _jsx("button", Object.assign({ type: "button", onClick: refresh, disabled: isLoading, title: "\uBCF4\uC548\uBB38\uC790 \uC0C8\uB85C\uACE0\uCE68", style: {
                                    flex: "0 0 auto",
                                    width: "36px",
                                    height: "36px",
                                    border: "1px solid #d8d8d8",
                                    borderRadius: "4px",
                                    background: "#fff",
                                    cursor: isLoading ? "default" : "pointer",
                                    fontSize: "16px",
                                    lineHeight: "1",
                                } }, { children: "\u21BB" }))] })), _jsx("div", Object.assign({ className: "input__area" }, { children: _jsx("input", { type: "text", id: "captchaValue", inputMode: "numeric", autoComplete: "off", maxLength: 6, placeholder: placeholder, style: { width: "100%", boxSizing: "border-box" }, className: isError ? "input-error" : "", value: value, 
                            // 숫자만 입력받는다 (서버가 숫자 6자리로 생성)
                            onChange: (e) => onChange(e.target.value.replace(/[^0-9]/g, "")), onKeyDown: onKeyDown }) }))] }))] })));
}
export default Captcha;
